import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Phone, Plus, Trash2 } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import { Fab } from '@shared/ui/primitives/Fab';
import { toast } from '@shared/ui/feedback/Toast';
import { fetchPanicContacts, deletePanicContact, type PanicContact } from './api';

type PanicContactsPanelProps = {
  /** Where to return after add/edit (e.g. `/app/panic?tab=contacts`). */
  returnTo?: string;
};

/**
 * Emergency-contacts list — navigates to full-page add/edit forms (no bottom sheet).
 */
export function PanicContactsPanel({ returnTo = '/app/panic?tab=contacts' }: PanicContactsPanelProps): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const contactsQuery = useQuery({ queryKey: ['panic-contacts'], queryFn: fetchPanicContacts });

  const deleteMutation = useMutation({
    mutationFn: deletePanicContact,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['panic-contacts'] });
      toast.success('Contact removed');
    },
    onError: () => toast.danger('Could not remove', 'Please try again.'),
  });

  const openNew = () => {
    navigate(`/app/panic/contacts/new?returnTo=${encodeURIComponent(returnTo)}`);
  };

  const openEdit = (contact: PanicContact) => {
    navigate(
      `/app/panic/contacts/${contact.contact_id}/edit?returnTo=${encodeURIComponent(returnTo)}`,
    );
  };

  if (contactsQuery.isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
    );
  }
  if (contactsQuery.isError) {
    return <ErrorState onRetry={() => contactsQuery.refetch()} />;
  }

  const contacts = contactsQuery.data ?? [];

  return (
    <div className="flex flex-col gap-3 pb-24">
      <p className="text-body-sm text-ink-secondary">
        These people are notified instantly when you raise an SOS.
      </p>

      {contacts.length === 0 ? (
        <EmptyState
          icon={<Phone className="h-7 w-7" strokeWidth={1.5} />}
          title="No contacts yet."
          description="Add at least one person who should know if you raise an alert."
          action={
            <Button onClick={openNew}>
              <Plus className="h-4 w-4" strokeWidth={1.75} />
              Add contact
            </Button>
          }
        />
      ) : (
        <>
          {contacts.map((contact) => (
            <Card
              key={contact.contact_id}
              asChild
              noGrain
              className="rounded-xl p-3.5"
            >
              <article className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => openEdit(contact)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
                    <Phone className="h-4.5 w-4.5" strokeWidth={1.75} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="block font-display text-headline leading-tight text-ink truncate">
                        {contact.name}
                      </span>
                      <span className="rounded-pill bg-sunken px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-ink-secondary">
                        {contact.contact_source === 'core_user' ? 'Platform user' : 'Outside contact'}
                      </span>
                      {!contact.is_active ? (
                        <span className="rounded-pill bg-danger/10 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-danger">
                          Inactive
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block text-body-sm text-ink-secondary truncate">
                      {[contact.mobile, contact.email].filter(Boolean).join(' · ')}
                    </span>
                    <span className="mt-1 block text-caption text-ink-muted">
                      Channels: {[
                        contact.notify_via_sms ? 'SMS' : null,
                        contact.notify_via_email ? 'Email' : null,
                        contact.notify_via_push ? 'Push' : null,
                      ].filter(Boolean).join(' · ') || 'None'}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${contact.name}`}
                  onClick={() => {
                    if (confirm('Remove this contact?')) deleteMutation.mutate(contact.contact_id);
                  }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </article>
            </Card>
          ))}
        </>
      )}

      <Fab onClick={openNew} label="Add contact">
        <Plus className="h-6 w-6" strokeWidth={2} />
      </Fab>
    </div>
  );
}
